import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import type { SocialProviderName } from '@repo/auth-contracts';
import { Alert } from '@/components/alert';
import { AuthCard } from '@/components/auth-card';
import { Button } from '@/components/button';
import { Divider } from '@/components/divider';
import { PageFooter } from '@/components/page-footer';
import { PasskeyMark } from '@/components/passkey-mark';
import { SocialButtons } from '@/components/social-buttons';
import { Spinner } from '@/components/spinner';
import { browserPreferences } from '@/lib/locale';
import { useLocale } from '@/lib/locale-context';
import {
  createInteractionApi,
  InteractionApiError,
  type Completion,
} from '@/lib/interaction-api';
import { copyFor, messageFor } from '@/lib/messages';
import { errorHome, type ErrorHome } from '@/lib/error-home';
import { env } from '@/lib/env';
import { CodeStep } from './steps/code-step';
import { ConsentStep } from './steps/consent-step';
import { consentIsIncremental } from '@/lib/consent-scopes';
import { EmailStep } from './steps/email-step';
import { OfferPasswordStep } from './steps/offer-password-step';
import { RequireBirthdateStep } from './steps/require-birthdate-step';
import { RequireTermsStep } from './steps/require-terms-step';
import { PasswordStep } from './steps/password-step';
import {
  CompleteProfileStep,
  type ProfileValues,
} from './steps/complete-profile-step';
import {
  flowReducer,
  initialFlowState,
  isNavigableStep,
  isPostIdentityStep,
  type Step,
} from './flow-state';

function errorCodeOf(error: unknown): string {
  return error instanceof InteractionApiError ? error.code : 'unknown';
}

/**
 * `sessionStorage` is not always reachable — Safari's private mode and locked
 * down enterprise profiles throw on access rather than returning null. This is
 * the sign-in page, so an uncaught throw here is a blank screen and no way in.
 * Losing the flag only costs the Back button some precision.
 */
function readFlag(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Ignored on purpose; see readFlag.
  }
}

/** The browser throws these when the user dismisses the platform prompt. */
function isCeremonyCancelled(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

/**
 * The render-only state machine behind `/interaction/:uid` (ADR 0010): it
 * reflects what the interaction API reports and forwards user input. It never
 * decides anything about authentication itself.
 */
// Annotated on purpose. Without it the switch below infers `| undefined` for
// an unhandled step, and a missing screen renders as a blank page instead of
// failing to compile — which is exactly how the social-pending screen once
// went missing.
export function InteractionFlow({ uid }: { uid: string }): ReactElement {
  const { locale, chosen } = useLocale();
  const copy = useMemo(() => copyFor(locale), [locale]);
  const api = useMemo(() => createInteractionApi(env.authApiUrl), []);
  const [state, dispatch] = useReducer(flowReducer, initialFlowState);
  const [email, setEmail] = useState('');
  const [passkeyAutofill, setPasskeyAutofill] = useState(false);
  /**
   * Set once the flow moves past a proven identity. Kept in sessionStorage as
   * well as a ref so it survives the reload a bfcache restore triggers, and
   * scoped to this interaction so a later sign-in in the same tab starts fresh.
   */
  const identityKey = useMemo(() => `buds-auth:identity-proven:${uid}`, [uid]);
  const identityProven = useRef(readFlag(identityKey));

  // The social callback returns here with its outcome in the query string.
  // Read it once, then scrub it: a reload should not replay a stale outcome,
  // and the value has no business staying in the address bar or in history.
  const [socialOutcome] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pending = params.get('social');
    const failed = params.get('social_error');
    if (pending || failed) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    return { pending, failed };
  });

  /**
   * Keeps the Back button inside the flow.
   *
   * Every step lives at the same `/interaction/:uid` URL, so without this the
   * browser has exactly one entry for the whole sign-in and Back means "leave",
   * discarding the user's progress from any step — including the code screen,
   * which already offers "use a different email" as an in-app equivalent.
   *
   * Only `NAVIGABLE_STEPS` push an entry. Later steps deliberately push none,
   * so Back from them pops to the last pre-authentication view rather than to a
   * screen whose submit is already spent.
   */
  useEffect(() => {
    const kind = state.step.kind;
    const recorded = (window.history.state as { step?: string } | null)?.step;

    if (isPostIdentityStep(kind)) {
      // Passing the identity boundary retires every pre-authentication entry,
      // not just this one: a longer session can hold several `code` entries
      // (each "use a different email" pushes a new pair), and rewriting only
      // the current one leaves the older ones restorable further back. The
      // marker is read on restore, so all of them collapse to the email form.
      identityProven.current = true;
      writeFlag(identityKey);
      if (recorded && recorded !== 'email') {
        window.history.replaceState({ step: 'email' }, '');
      }
      return;
    }

    // `loading` and `error` are neither navigable nor past identity: they
    // record nothing and retire nothing.
    if (!isNavigableStep(kind) || recorded === kind) return;

    const entry = {
      step: kind,
      ...(state.step.kind === 'code' ? { email: state.step.email } : {}),
    };
    // The first navigable step of the session adopts the entry the browser is
    // already on; pushing there would put a phantom entry between the flow and
    // wherever the user came from.
    if (recorded === undefined) {
      window.history.replaceState(entry, '');
    } else {
      window.history.pushState(entry, '');
    }
  }, [state.step, identityKey]);

  /**
   * A page restored from the back/forward cache is the *same* document: React
   * never unmounts, no effect re-runs, and every request the flow made is long
   * finished. Whatever was on screen when the browser froze it comes back
   * verbatim — most visibly the "Signing you in…" spinner after a completed
   * login, which would sit there forever, but also a social hand-off frozen
   * mid-`pending` with every control disabled, and a CSRF token the server has
   * since re-minted.
   *
   * Reloading is the only honest answer: the interaction is either still live,
   * in which case the flow starts clean, or spent, in which case the user gets
   * the "start again" screen instead of a spinner that never resolves.
   */
  useEffect(() => {
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) window.location.reload();
    }
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    function onPopState(event: PopStateEvent) {
      const entry = event.state as { step?: string; email?: string } | null;
      // No entry of ours means the browser is leaving the document; let it.
      if (!entry?.step || !isNavigableStep(entry.step as Step['kind'])) return;
      // Once an identity has been proven this session, no `code` entry can be
      // honoured: that code is spent, so the screen would reject the only
      // value the user has. The email form is the honest landing place.
      const restored: Step =
        entry.step === 'code' && !identityProven.current
          ? { kind: 'code', email: entry.email ?? email }
          : entry.step === 'code'
            ? { kind: 'email' }
            : ({ kind: entry.step } as Step);
      dispatch({ type: 'restore', step: restored });
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [email]);

  /** Completion always hands the browser back to the OP's resume URL. */
  const finish = useCallback((redirectTo: string) => {
    dispatch({ type: 'finished' });
    window.location.assign(redirectTo);
  }, []);

  /**
   * Every login-completing call can answer with a post-login offer instead of
   * a redirect, and the offers chain — declining a passkey may still lead to
   * the password one. One place decides, so no call site has to remember.
   */
  const settle = useCallback(
    (result: Completion) => {
      if (result.status === 'offer-passkey') {
        dispatch({ type: 'offer-passkey' });
        return;
      }
      if (result.status === 'offer-password') {
        dispatch({ type: 'offer-password' });
        return;
      }
      if (result.status === 'require-terms') {
        dispatch({ type: 'require-terms', documents: result.documents });
        return;
      }
      if (result.status === 'require-birthdate') {
        dispatch({ type: 'require-birthdate' });
        return;
      }
      finish(result.redirectTo);
    },
    [finish],
  );

  const requireCsrf = useCallback((): string | null => {
    if (!state.csrfToken) {
      dispatch({ type: 'fatal', code: 'csrf' });
      return null;
    }
    return state.csrfToken;
  }, [state.csrfToken]);

  const startEmailLogin = useCallback(
    async (address: string, resend = false) => {
      const csrfToken = requireCsrf();
      if (!csrfToken) return;
      dispatch({ type: 'submit' });
      try {
        await api.startEmailLogin(uid, address, csrfToken, locale);
        setEmail(address);
        dispatch(
          resend
            ? { type: 'code-resent' }
            : { type: 'code-sent', email: address },
        );
      } catch (error) {
        dispatch({ type: 'failed', code: errorCodeOf(error) });
      }
    },
    [api, locale, requireCsrf, uid],
  );

  /**
   * Denies the interaction so the relying party gets access_denied instead of
   * a window that was silently abandoned — /abort existed fully built and had
   * no caller. Offered wherever the flow cannot proceed and there is no Back to
   * fall back on (worst of all inside an app webview with no window chrome):
   * the under-age refusal on the signup profile and the age gate, and declining
   * the mandatory terms gate.
   */
  const abortInteraction = useCallback(async () => {
    const csrfToken = requireCsrf();
    if (!csrfToken) return;
    dispatch({ type: 'submit' });
    try {
      const { redirectTo } = await api.abort(uid, csrfToken);
      finish(redirectTo);
    } catch (error) {
      dispatch({ type: 'failed', code: errorCodeOf(error) });
    }
  }, [api, finish, requireCsrf, uid]);

  /**
   * The escape for someone who is not the account named on the re-gate terms
   * screen. Aborting would NOT do: the RP gets access_denied, the session is
   * untouched, and the very next authorization re-gates the same account —
   * a loop. Ending the session is what actually lets a different person sign
   * in as themselves (ADR 0037). The issuer's sign-out screen asks to confirm,
   * so a misclick is recoverable.
   */
  const signOutAndRestart = useCallback(() => {
    window.location.assign(`${env.authApiUrl}/session/end`);
  }, []);

  const completeProfile = useCallback(
    async (values: ProfileValues) => {
      const csrfToken = requireCsrf();
      if (!csrfToken) return;
      dispatch({ type: 'submit' });
      try {
        const { redirectTo } = await api.completeProfile(
          uid,
          // The active UI language (chosen or detected) outranks the raw
          // navigator value: the account should start in the language the
          // user is actually reading.
          { ...values, ...browserPreferences(), locale },
          csrfToken,
        );
        finish(redirectTo);
      } catch (error) {
        dispatch({ type: 'failed', code: errorCodeOf(error) });
      }
    },
    [api, finish, locale, requireCsrf, uid],
  );

  const verifyCode = useCallback(
    async (code: string) => {
      const csrfToken = requireCsrf();
      if (!csrfToken) return;
      dispatch({ type: 'submit' });
      try {
        const result = await api.verifyEmailCode(
          uid,
          code,
          csrfToken,
          // Explicit selection only: a detected browser language must never
          // overwrite the account's stored preference (contract note).
          chosen ? locale : undefined,
        );
        if (result.status === 'profile-required') {
          dispatch({ type: 'profile-required' });
          return;
        }
        settle(result);
      } catch (error) {
        dispatch({ type: 'failed', code: errorCodeOf(error) });
      }
    },
    [api, chosen, locale, requireCsrf, settle, uid],
  );

  const passwordLogin = useCallback(
    async (address: string, password: string) => {
      const csrfToken = requireCsrf();
      if (!csrfToken) return;
      dispatch({ type: 'submit' });
      try {
        const result = await api.passwordLogin(
          uid,
          address,
          password,
          csrfToken,
          chosen ? locale : undefined,
        );
        setEmail(address);
        // Not `finish` directly: this route can now answer with the mandatory
        // age gate, which is exactly the population that arrives here with an
        // imported ZITADEL hash (ADR 0021).
        settle(result);
      } catch (error) {
        dispatch({ type: 'failed', code: errorCodeOf(error) });
      }
    },
    [api, chosen, locale, requireCsrf, settle, uid],
  );

  const supplyBirthdate = useCallback(
    async (birthdate: string) => {
      const csrfToken = requireCsrf();
      if (!csrfToken) return;
      dispatch({ type: 'submit' });
      try {
        settle(await api.supplyBirthdate(uid, birthdate, csrfToken));
      } catch (error) {
        // An under-age answer lands here and leaves the step in place: the
        // held login survives it, so a mistyped year is correctable (ADR 0021).
        dispatch({ type: 'failed', code: errorCodeOf(error) });
      }
    },
    [api, requireCsrf, settle, uid],
  );

  const acceptTerms = useCallback(async () => {
    const csrfToken = requireCsrf();
    if (!csrfToken) return;
    dispatch({ type: 'submit' });
    try {
      // settle, not finish: accepting re-enters the funnel, so the optional
      // passkey/password nudges still get their turn on this login.
      settle(await api.acceptTerms(uid, csrfToken));
    } catch (error) {
      // The held login survives a failure, so the step stays on screen.
      dispatch({ type: 'failed', code: errorCodeOf(error) });
    }
  }, [api, requireCsrf, settle, uid]);

  const setPassword = useCallback(
    async (password: string) => {
      const csrfToken = requireCsrf();
      if (!csrfToken) return;
      dispatch({ type: 'submit' });
      try {
        const { redirectTo } = await api.setPassword(uid, password, csrfToken);
        finish(redirectTo);
      } catch (error) {
        dispatch({ type: 'failed', code: errorCodeOf(error) });
      }
    },
    [api, finish, requireCsrf, uid],
  );

  const skipPassword = useCallback(async () => {
    const csrfToken = requireCsrf();
    if (!csrfToken) return;
    dispatch({ type: 'submit' });
    try {
      const { redirectTo } = await api.skipPassword(uid, csrfToken);
      finish(redirectTo);
    } catch (error) {
      dispatch({ type: 'failed', code: errorCodeOf(error) });
    }
  }, [api, finish, requireCsrf, uid]);

  const completeSocial = useCallback(
    async (csrfToken: string) => {
      // A dedicated "Signing you in…" card, not `submit` — otherwise the email
      // form the cold load rendered stays on screen with its "Send code" button
      // spinning for the whole redeem (retries included), which reads as social
      // login having failed.
      dispatch({ type: 'completing-social' });
      // The `?social=complete` outcome was scrubbed from the URL on first read
      // (a reload must not replay it), so this in-memory call is the only
      // direct path to the login held server-side. A single transient hiccup
      // must therefore not strand it: retry briefly before giving up. A
      // definitive 4xx is not transient — repeating it re-begs the same
      // answer. If all attempts fail, the inline error still leaves the
      // heavyweight recovery of clicking the provider button again, which
      // runs a fresh handshake.
      let lastError: unknown;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 700 * attempt));
        }
        try {
          settle(await api.completeSocial(uid, csrfToken));
          return;
        } catch (error) {
          lastError = error;
          // Definitive means a real 4xx verdict. Status 0 is the api layer's
          // could-not-reach marker — the very case this loop exists for — and
          // 429/5xx may pass on a later attempt.
          if (
            error instanceof InteractionApiError &&
            error.status >= 400 &&
            error.status < 500 &&
            error.status !== 429
          ) {
            break;
          }
        }
      }
      // Definitive: drop back to the email form with the error rather than
      // leaving the user on a spinner card that will never resolve.
      dispatch({ type: 'social-redeem-failed', code: errorCodeOf(lastError) });
    },
    [api, settle, uid],
  );

  /**
   * Takes the token as an argument instead of reading it from state, which is
   * what lets BOTH callers work: the buttons pass the state token via
   * `startSocial`, and the idp_hint auto-start passes the token straight off
   * the `loaded` payload — at that moment the dispatch has not committed yet,
   * so state still holds null and reading it would die on the csrf error
   * screen before the flow ever reached the provider. Stable identity
   * ([api, uid]) is what lets the load effect list it as a dependency without
   * re-running itself.
   */
  const beginSocial = useCallback(
    async (provider: SocialProviderName, csrfToken: string) => {
      dispatch({ type: 'submit' });
      try {
        const authorizationUrl = await api.startSocial(
          uid,
          provider,
          csrfToken,
        );
        // A full navigation: the provider owns the next screen.
        window.location.assign(authorizationUrl);
      } catch (error) {
        dispatch({ type: 'failed', code: errorCodeOf(error) });
      }
    },
    [api, uid],
  );

  const startSocial = useCallback(
    (provider: SocialProviderName) => {
      const csrfToken = requireCsrf();
      if (!csrfToken) return;
      void beginSocial(provider, csrfToken);
    },
    [beginSocial, requireCsrf],
  );

  /**
   * Arms the browser's passkey autofill.
   *
   * This is the whole reason a returning user never has to type anything:
   * the request sits open against the email field, and choosing a passkey
   * from the browser's own autofill menu resolves it. It is deliberately
   * silent — no spinner, no state change — because nothing is happening from
   * the user's point of view until they pick one.
   */
  const armPasskeyAutofill = useCallback(
    async (csrfToken: string) => {
      const { browserSupportsWebAuthnAutofill, startAuthentication } =
        await import('@simplewebauthn/browser');
      if (!(await browserSupportsWebAuthnAutofill())) return;

      try {
        const options = await api.passkeyOptions(
          uid,
          'passkey/start',
          csrfToken,
        );
        const response = await startAuthentication({
          optionsJSON: options as never,
          useBrowserAutofill: true,
        });
        settle(
          await api.passkeyFinish(
            uid,
            'passkey/finish',
            response as unknown as Record<string, unknown>,
            csrfToken,
          ),
        );
      } catch {
        // Abandoned, aborted by another ceremony, or unsupported after all.
        // There is nothing to report: the user did not ask for this.
        setPasskeyAutofill(false);
      }
    },
    [api, settle, uid],
  );

  const passkeyLogin = useCallback(async () => {
    const csrfToken = requireCsrf();
    if (!csrfToken) return;
    dispatch({ type: 'submit' });
    try {
      const options = await api.passkeyOptions(uid, 'passkey/start', csrfToken);
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const response = await startAuthentication({
        optionsJSON: options as never,
      });
      settle(
        await api.passkeyFinish(
          uid,
          'passkey/finish',
          response as unknown as Record<string, unknown>,
          csrfToken,
        ),
      );
    } catch (error) {
      // Dismissing the platform prompt throws; that is a choice, not a fault.
      dispatch({
        type: 'failed',
        code: isCeremonyCancelled(error)
          ? 'passkey_cancelled'
          : errorCodeOf(error),
      });
    }
  }, [api, requireCsrf, settle, uid]);

  const enrolPasskey = useCallback(async () => {
    const csrfToken = requireCsrf();
    if (!csrfToken) return;
    dispatch({ type: 'submit' });
    try {
      const options = await api.passkeyOptions(
        uid,
        'passkey/enrol/start',
        csrfToken,
      );
      const { startRegistration } = await import('@simplewebauthn/browser');
      const response = await startRegistration({
        optionsJSON: options as never,
      });
      settle(
        await api.passkeyFinish(
          uid,
          'passkey/enrol/finish',
          response as unknown as Record<string, unknown>,
          csrfToken,
        ),
      );
    } catch (error) {
      if (isCeremonyCancelled(error)) {
        // The login is already real; declining at the prompt should not
        // strand the user on an offer screen.
        dispatch({ type: 'failed', code: 'passkey_cancelled' });
        return;
      }
      dispatch({ type: 'failed', code: errorCodeOf(error) });
    }
  }, [api, requireCsrf, settle, uid]);

  const skipPasskey = useCallback(async () => {
    const csrfToken = requireCsrf();
    if (!csrfToken) return;
    dispatch({ type: 'submit' });
    try {
      settle(
        await api.passkeyFinish(uid, 'passkey/enrol/skip', null, csrfToken),
      );
    } catch (error) {
      dispatch({ type: 'failed', code: errorCodeOf(error) });
    }
  }, [api, requireCsrf, settle, uid]);

  const decideConsent = useCallback(
    async (decision: 'allow' | 'deny') => {
      const csrfToken = requireCsrf();
      if (!csrfToken) return;
      dispatch({ type: 'submit' });
      try {
        const { redirectTo } = await api.decideConsent(
          uid,
          decision,
          csrfToken,
        );
        finish(redirectTo);
      } catch (error) {
        dispatch({ type: 'failed', code: errorCodeOf(error) });
      }
    },
    [api, finish, requireCsrf, uid],
  );

  useEffect(() => {
    let cancelled = false;
    api
      .getState(uid)
      .then((loaded) => {
        if (cancelled) return;
        dispatch({ type: 'loaded', state: loaded });
        // The address the resumed step is about — resend and the profile
        // subtitle both read it. Only the code and sign-up steps carry one; the
        // held-login steps (offers, gates) have no address to seed.
        if (
          loaded.pending?.step === 'code' ||
          loaded.pending?.step === 'complete-profile'
        ) {
          setEmail(loaded.pending.email);
        }

        // `idp_hint`: the client asked for deep entry to one provider, so go
        // straight there instead of showing the method menu. Guarded three
        // ways against a redirect loop, because the hint lives in the
        // authorize params and is therefore in EVERY load of this
        // interaction: never after a social outcome (a failed callback must
        // land on the full screen, not bounce back out), never over a
        // resumable step, and at most once per interaction (the flag), so a
        // user who cancels at the provider and reloads gets the menu.
        const hintKey = `buds-auth:idp-hint-fired:${uid}`;
        if (
          loaded.idpHint &&
          loaded.prompt === 'login' &&
          !loaded.pending &&
          !socialOutcome.pending &&
          !socialOutcome.failed &&
          !readFlag(hintKey)
        ) {
          writeFlag(hintKey);
          void beginSocial(loaded.idpHint, loaded.csrfToken);
          return;
        }
        // `profile_required` is not a pending state — the server already put a
        // pending sign-up behind it, so the profile step is the right screen.
        // Only on the cold login screen: any other step means the user is
        // already partway through something.
        if (
          loaded.prompt === 'login' &&
          loaded.passkeysEnabled &&
          !socialOutcome.pending &&
          !socialOutcome.failed
        ) {
          setPasskeyAutofill(true);
          void armPasskeyAutofill(loaded.csrfToken);
        }

        if (socialOutcome.pending === 'complete') {
          // The callback held a finished login; redeem it from here, where the
          // interaction cookie is in scope.
          void completeSocial(loaded.csrfToken);
        } else if (socialOutcome.pending === 'profile_required') {
          dispatch({ type: 'profile-required' });
        } else if (socialOutcome.pending) {
          dispatch({ type: 'social-pending', reason: socialOutcome.pending });
        } else if (socialOutcome.failed) {
          dispatch({ type: 'failed', code: 'social_failed' });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled)
          dispatch({ type: 'load-failed', code: errorCodeOf(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [
    api,
    armPasskeyAutofill,
    beginSocial,
    completeSocial,
    socialOutcome,
    uid,
  ]);

  const inlineError = state.errorCode
    ? messageFor(state.errorCode, locale)
    : null;
  // Where the current error belongs: a specific field, or the step's top alert.
  const home = state.errorCode ? errorHome(state.errorCode) : null;
  // For a step that OWNS `kind`, the error shows on its field; otherwise it is
  // a form-level alert. Exactly one slot is ever non-null, so the two channels
  // never announce the same error twice.
  const errorSlots = (kind: ErrorHome) => ({
    fieldError: home === kind ? inlineError : null,
    formError: home === kind ? null : inlineError,
  });
  const footer = <PageFooter />;

  switch (state.step.kind) {
    case 'loading':
      return (
        <AuthCard title={copy.emailTitle} footer={footer}>
          <div className="flex justify-center py-4 text-buds-muted">
            <Spinner label={copy.finishing} />
          </div>
        </AuthCard>
      );

    case 'email':
      return (
        <AuthCard
          title={copy.emailTitle}
          subtitle={copy.emailSubtitle}
          footer={footer}
        >
          <EmailStep
            copy={copy}
            pending={state.pending}
            {...errorSlots('email')}
            passkeyAutofill={passkeyAutofill}
            onSubmit={(address) => void startEmailLogin(address)}
          />

          {/* Everything below is another way to do the same thing, so it all
              gets the same weight. The email form above is first only because
              it is the one method every single person can use. The password
              sits last within this group: still a peer you can reach in one
              click, still the least preferred (ADR 0007). */}
          <Divider label={copy.orDivider} />
          <div className="space-y-3">
            {state.passkeysEnabled ? (
              <Button
                type="button"
                variant="outline"
                icon={<PasskeyMark />}
                onClick={() => void passkeyLogin()}
                disabled={state.pending}
              >
                {copy.passkeyCta}
              </Button>
            ) : null}
            <SocialButtons
              copy={copy}
              providers={state.socialProviders as SocialProviderName[]}
              pending={state.pending}
              onSelect={(provider) => void startSocial(provider)}
            />
            {/* In the group, but a link rather than a pill: it belongs with
                the alternatives, and it is still the one you reach for when
                the others are out of reach (ADR 0007). */}
            <Button
              type="button"
              variant="link"
              className="pt-2"
              onClick={() => dispatch({ type: 'show-password' })}
              disabled={state.pending}
            >
              {copy.usePasswordInstead}
            </Button>
          </div>

          {/* Last and quietest: the signpost for people who do not yet know
              this same form would have signed them up (ADR 0019). */}
          <div className="mt-7 border-t border-buds-dark/10 pt-5 text-center">
            <Button
              type="button"
              variant="link"
              onClick={() => dispatch({ type: 'show-signup' })}
              disabled={state.pending}
            >
              {copy.signInSwitchToSignup}
            </Button>
          </div>
        </AuthCard>
      );

    case 'signup':
      return (
        <AuthCard
          title={copy.signupTitle}
          subtitle={copy.signupSubtitle}
          footer={footer}
        >
          {/* The same form, the same endpoint, the same server response — only
              the wording differs (ADR 0019). Branching here on whether the
              address exists is exactly what we must never do. */}
          <EmailStep
            copy={copy}
            pending={state.pending}
            {...errorSlots('email')}
            onSubmit={(address) => void startEmailLogin(address)}
          />

          {/* Social can create an account, so it belongs here. A passkey
              cannot — it needs an account to attach to — and neither can the
              password fallback, so both are absent rather than offered and
              then refused. */}
          {state.socialProviders.length > 0 ? (
            <>
              <Divider label={copy.orDivider} />
              <div className="space-y-3">
                <SocialButtons
                  copy={copy}
                  providers={state.socialProviders as SocialProviderName[]}
                  pending={state.pending}
                  onSelect={(provider) => void startSocial(provider)}
                />
              </div>
            </>
          ) : null}

          <div className="mt-7 border-t border-buds-dark/10 pt-5 text-center">
            <Button
              type="button"
              variant="link"
              onClick={() => dispatch({ type: 'show-email' })}
              disabled={state.pending}
            >
              {copy.signupSwitchToSignIn}
            </Button>
          </div>
        </AuthCard>
      );

    case 'social-pending':
      return (
        <AuthCard
          title={copy.socialPendingTitle}
          subtitle={copy.socialPendingBody(state.step.reason)}
          footer={footer}
        >
          {/* The way forward is always the same: prove the mailbox. Resolving
              it attaches the social identity to whichever account that login
              lands on (docs/auth/10-concepts/account-linking.md). No passkey
              or social alternatives here — one of them is what got us to this
              screen, and none of them can settle the question being asked. */}
          <EmailStep
            copy={copy}
            pending={state.pending}
            {...errorSlots('email')}
            submitLabel={copy.socialPendingContinue}
            onSubmit={(address) => void startEmailLogin(address)}
          />
        </AuthCard>
      );

    case 'password':
      return (
        <AuthCard
          title={copy.passwordTitle}
          subtitle={copy.passwordSubtitle}
          footer={footer}
        >
          <PasswordStep
            copy={copy}
            pending={state.pending}
            error={inlineError}
            initialEmail={email}
            onSubmit={(address, password) =>
              void passwordLogin(address, password)
            }
            onUseCode={() => dispatch({ type: 'show-email' })}
          />
        </AuthCard>
      );

    case 'offer-passkey':
      return (
        <AuthCard
          title={copy.passkeyOfferTitle}
          subtitle={copy.passkeyOfferSubtitle}
          footer={footer}
        >
          {inlineError ? (
            <div className="mb-5">
              <Alert tone="error">{inlineError}</Alert>
            </div>
          ) : null}
          <div className="space-y-3">
            <Button
              type="button"
              loading={state.pending}
              onClick={() => void enrolPasskey()}
            >
              {copy.passkeyOfferSubmit}
            </Button>
            <Button
              type="button"
              variant="link"
              onClick={() => void skipPasskey()}
              disabled={state.pending}
            >
              {copy.passkeyOfferSkip}
            </Button>
          </div>
        </AuthCard>
      );

    case 'require-terms':
      return (
        <AuthCard
          title={copy.termsRequiredTitle}
          subtitle={copy.termsRequiredSubtitle}
          footer={footer}
        >
          <RequireTermsStep
            copy={copy}
            pending={state.pending}
            error={inlineError}
            documents={state.step.documents}
            account={state.step.account}
            onSignOut={signOutAndRestart}
            onSubmit={acceptTerms}
          />
          {/* Declining is leaving: no token is issued until both documents are
              accepted, so the way out is to deny the authorization — the RP
              gets access_denied and nothing is written to the account
              (terms-acceptance.md AC3). Shown unconditionally: a user may
              always choose not to accept. */}
          <div className="mt-7 flex flex-col items-center gap-2 border-t border-buds-dark/10 pt-5">
            <Button
              type="button"
              variant="link"
              onClick={() => void abortInteraction()}
              disabled={state.pending}
            >
              {copy.returnToApp}
            </Button>
          </div>
        </AuthCard>
      );

    case 'require-birthdate':
      return (
        <AuthCard
          title={copy.birthdateRequiredTitle}
          subtitle={copy.birthdateRequiredSubtitle}
          footer={footer}
        >
          <RequireBirthdateStep
            copy={copy}
            pending={state.pending}
            {...errorSlots('birthdate')}
            minimumAge={state.minimumAge}
            onSubmit={(birthdate) => void supplyBirthdate(birthdate)}
          />
          {state.errorCode === 'under_minimum_age' ? (
            // Only once a truly under-age user is refused: an adult's mistyped
            // date is still correctable in place. This is a post-login gate
            // with no "back to email", so the only exit is to deny the
            // interaction — the RP gets access_denied rather than an abandoned
            // window, worst of all inside an app webview with no chrome.
            <div className="mt-7 flex flex-col items-center gap-2 border-t border-buds-dark/10 pt-5">
              <Button
                type="button"
                variant="link"
                onClick={() => void abortInteraction()}
                disabled={state.pending}
              >
                {copy.returnToApp}
              </Button>
            </div>
          ) : null}
        </AuthCard>
      );

    case 'offer-password':
      return (
        <AuthCard
          title={copy.passwordSetTitle}
          subtitle={copy.passwordSetSubtitle}
          footer={footer}
        >
          <OfferPasswordStep
            copy={copy}
            pending={state.pending}
            {...errorSlots('password')}
            onSet={(password) => void setPassword(password)}
            onSkip={() => void skipPassword()}
          />
        </AuthCard>
      );

    case 'code': {
      // Bind the narrowed step so the callbacks below keep the type.
      const codeStep = state.step;
      return (
        <AuthCard
          title={copy.codeTitle}
          subtitle={copy.codeSubtitle(codeStep.email)}
          footer={footer}
        >
          <CodeStep
            copy={copy}
            pending={state.pending}
            {...errorSlots('code')}
            notice={state.notice === 'code-resent' ? copy.codeResent : null}
            onSubmit={(code) => void verifyCode(code)}
            onResend={() => void startEmailLogin(email || codeStep.email, true)}
            onRestart={() => dispatch({ type: 'restart' })}
          />
        </AuthCard>
      );
    }

    case 'complete-profile':
      return (
        <AuthCard
          title={copy.profileTitle}
          subtitle={copy.profileSubtitle(email)}
          footer={footer}
        >
          <CompleteProfileStep
            copy={copy}
            pending={state.pending}
            error={inlineError}
            errorCategory={home}
            minimumAge={state.minimumAge}
            prefill={state.profilePrefill}
            onSubmit={(values) => void completeProfile(values)}
          />
          {/* The one step that could not be left: unlike its siblings it had
              no way out, which stranded anyone whose age the gate refuses —
              and in an app webview there is no Back button to fall back on.
              The pending sign-up survives server-side, so this is a view
              change, not an abandonment. */}
          <div className="mt-7 flex flex-col items-center gap-2 border-t border-buds-dark/10 pt-5">
            <Button
              type="button"
              variant="link"
              onClick={() => dispatch({ type: 'restart' })}
              disabled={state.pending}
            >
              {copy.back}
            </Button>
            {state.errorCode === 'under_minimum_age' ? (
              // A truly under-age user cannot satisfy this form, and the RP
              // deserves an answer: this denies the interaction so it gets
              // access_denied rather than an abandoned window.
              <Button
                type="button"
                variant="link"
                onClick={() => void abortInteraction()}
                disabled={state.pending}
              >
                {copy.returnToApp}
              </Button>
            ) : null}
          </div>
        </AuthCard>
      );

    case 'consent':
      return (
        <AuthCard
          title={copy.consentTitle}
          subtitle={
            consentIsIncremental(state.step.scopes, state.step.grantedScopes)
              ? copy.consentSubtitleMore(state.step.clientName)
              : copy.consentSubtitle(state.step.clientName)
          }
          footer={footer}
        >
          <ConsentStep
            copy={copy}
            locale={locale}
            scopes={state.step.scopes}
            grantedScopes={state.step.grantedScopes}
            pending={state.pending}
            error={inlineError}
            onDecide={(decision) => void decideConsent(decision)}
          />
        </AuthCard>
      );

    // Both are the same "Signing you in…" card: `completing-social` while the
    // callback's held login is redeemed, `finishing` while the browser is sent
    // back to the RP. Identical to the user; the state distinction drives what
    // happens next (a redeem may still surface an offer or fall back to email).
    case 'completing-social':
    case 'finishing':
      return (
        <AuthCard title={copy.finishing} footer={footer}>
          <div className="flex justify-center py-4 text-buds-muted">
            <Spinner label={copy.finishing} />
          </div>
        </AuthCard>
      );

    case 'error': {
      // A stale interaction cannot be revived by reloading — the copy for that
      // code already says to start again from the app. Everything else that
      // lands here (a dropped connection, an expired CSRF token, a social
      // round trip that fell over) is worth one retry, and leaving the user on
      // a dead end with no control is worse than an occasional wasted click.
      const retryable = state.step.code !== 'interaction_not_found';
      return (
        <AuthCard title={copy.errorTitle} footer={footer}>
          {/* role="alert" because this card can swap in mid-flow: the heading
              takes focus (AuthCard), and the alert makes the WHY immediate
              rather than something to go hunting for. */}
          <p role="alert" className="text-sm leading-relaxed text-buds-muted">
            {messageFor(state.step.code, locale)}
          </p>
          {retryable ? (
            <div className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                {copy.errorRetry}
              </Button>
            </div>
          ) : null}
        </AuthCard>
      );
    }
  }
}
